import { reportReader$ } from './reportReader$';
import type { ReportReader$State, ReportSearchResults$State } from './types';
import { isEmpty, isEqual, pick } from 'lodash';
import {
  APIAggregatesResponse,
  APIReportFilters,
  ArdoqId,
  type GraphlakeSnapshotId,
} from '@ardoq/api-types';
import { ApiResponse, presentationApi, reportApi } from '@ardoq/api';
import { dispatchAction } from '@ardoq/rxbeach';
import { aggregatesWasTriggered } from './actions';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { ArdoqError, isArdoqError, Maybe, Result } from '@ardoq/common-helpers';
import { keepValidReaderStateFilters } from './utils';

const statePropertiesThatShouldTriggerAggregatesResultsToExecute: Array<
  keyof ReportSearchResults$State
> = [
  'filters',
  'reportId',
  'presentationId',
  'slideId',
  'hasFinishedFetchReport',
  'selectedAggregate',
  'columns',
  'reportAPIError',
  'selectedSnapshot',
] as const;

export type FetchAggregatesParams = {
  reportId: ArdoqId;
  filters: APIReportFilters | null;
  snapshot?: GraphlakeSnapshotId;
};

export type FetchPresentationAggregatesParams = FetchAggregatesParams & {
  presentationId: ArdoqId;
  slideId: ArdoqId;
};

export const fetchAggregateResults = async ({
  reportId,
  filters,
  snapshot,
}: FetchAggregatesParams): ApiResponse<APIAggregatesResponse> => {
  dispatchAction(aggregatesWasTriggered(true));
  const aggregateResults = await reportApi.fetchAggregates(reportId, {
    filters: isEmpty(filters) ? undefined : filters,
    snapshot,
  });
  dispatchAction(aggregatesWasTriggered(false));
  return aggregateResults;
};

export const fetchPresentationAggregatesResults = async ({
  reportId,
  filters,
  presentationId,
  slideId,
}: FetchPresentationAggregatesParams): ApiResponse<APIAggregatesResponse> => {
  dispatchAction(aggregatesWasTriggered(true));
  const aggregateResults = await presentationApi.fetchReportAggregates(
    presentationId,
    slideId,
    reportId,
    isEmpty(filters) ? undefined : filters
  );
  dispatchAction(aggregatesWasTriggered(false));
  return aggregateResults;
};

const pickStateThatShouldTriggerAggregatesResultsToExecute = (
  state: ReportReader$State
): ReportSearchResults$State =>
  pick(state, statePropertiesThatShouldTriggerAggregatesResultsToExecute);

const toApiRequest = (
  state: ReportSearchResults$State
): FetchAggregatesParams => ({
  reportId: state.reportId!,
  filters: state.filters,
  snapshot: state.selectedSnapshot ?? undefined,
});

const toPresentationApiRequest = (
  state: ReportSearchResults$State
): FetchPresentationAggregatesParams => ({
  reportId: state.reportId!,
  presentationId: state.presentationId!,
  slideId: state.slideId!,
  filters: state.filters,
});

const mapAggregates = (
  aggregateResults: Result<APIAggregatesResponse>
): {
  aggregateError: Maybe<ArdoqError>;
  aggregateResults: Maybe<APIAggregatesResponse>;
} => {
  if (isArdoqError(aggregateResults)) {
    return {
      aggregateError: aggregateResults,
      aggregateResults: null,
    };
  }

  return {
    aggregateError: null,
    aggregateResults: aggregateResults,
  };
};

const shouldFetchAggregates = (
  searchResults$State: ReportSearchResults$State
): boolean =>
  Boolean(
    searchResults$State.selectedAggregate &&
    searchResults$State.hasFinishedFetchReport &&
    !searchResults$State.reportAPIError
  );

export const aggregatesResults$ = reportReader$.pipe(
  map(keepValidReaderStateFilters),
  map(pickStateThatShouldTriggerAggregatesResultsToExecute),
  distinctUntilChanged<ReportSearchResults$State>(isEqual),
  filter(shouldFetchAggregates),
  map(toApiRequest),
  debounceTime(300),
  switchMap(fetchAggregateResults),
  map(mapAggregates),
  startWith({
    aggregateError: null,
    aggregateResults: null,
  })
);

export const presentationAggregatesResults$ = reportReader$.pipe(
  map(keepValidReaderStateFilters),
  map(pickStateThatShouldTriggerAggregatesResultsToExecute),
  distinctUntilChanged<ReportSearchResults$State>(isEqual),
  filter(
    (
      a: ReportSearchResults$State
    ): a is ReportSearchResults$State & {
      reportId: ArdoqId;
      presentationId: ArdoqId;
      slideId: ArdoqId;
    } =>
      Boolean(a.presentationId && a.slideId && a.reportId) &&
      shouldFetchAggregates(a)
  ),
  map(toPresentationApiRequest),
  switchMap(fetchPresentationAggregatesResults),
  map(mapAggregates),
  startWith({
    aggregateError: null,
    aggregateResults: null,
  })
);
